import React from "react";
import heroImage from "../image/background.jpg";
import Button from "../component/Button";
import { products, rawMaterials } from "../data/data";
import Card from "../component/Card";
import chicken from "../image/chicken.png";
import industry from "../image/industry.png";
import { useNavigate } from "react-router-dom";
import ContactForm from "../component/ContactForm";
//motion
import { motion } from "framer-motion";
//variants
import { fadeIn } from "../variants";


const Home = () => {
  const navigate = useNavigate();
   // Show only the first three products
   const limitedProducts = products.slice(0, 3);
   const limitedRawmaterial = rawMaterials.slice(0, 3);

  // Variants for animation
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2, // Animates child elements sequentially
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 30 },
    show: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };
  return (
    <div>
      <motion.div
        id="home"
        className="relative h-screen bg-cover bg-center flex items-center mt-20 md:mt-24 overflow-hidden"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-primary/40 to-black/70 animate-gradient-shift"></div>
        
        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Content Div */}
        <div className="relative z-10 text-white w-full lg:w-5/6 xl:w-4/6 h-full flex flex-col justify-center items-center lg:items-start px-6 sm:px-8 md:px-12 lg:px-16">
          <motion.div
            variants={fadeIn("down", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.7 }}
            className="text-center lg:text-left w-full"
          >
            <motion.h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold lg:w-[85%] leading-tight">
              Your Trusted Partner in{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-green-500">
                Animal Feed Solutions
              </span>
            </motion.h1>
            <motion.p className="text-lg sm:text-xl md:text-2xl py-6 lg:w-[60%] text-gray-100 leading-relaxed">
              Empowering farmers to achieve their goals while driving
              sustainable growth in agriculture.
            </motion.p>
            <motion.div className="flex items-center gap-3 mb-6">
              <div className="h-1 w-16 bg-gradient-primary rounded-full"></div>
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold italic text-green-300">
                Let's Grow Together
              </h4>
            </motion.div>
          </motion.div>

          <motion.div
            variants={fadeIn("up", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 mt-4 items-center"
          >
            <Button text="Explore Products" to="/services" variant="primary" />
            <Button text="Contact Us" to="/contact" variant="secondary" />
          </motion.div>
        </div>
      </motion.div>

      <div
        id="services"
        className="h-auto bg-white flex-col justify-center items-center px-6 md:px-16 lg:px-40"
      >
        {/* Products Section */}
        <div className="text-center mb-12 mt-20 pt-10">
          <motion.h2
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.7 }}
            className="text-3xl md:text-4xl font-bold text-black mb-4"
          >
            What <span className="gradient-text">We Sell</span>
          </motion.h2>
          <h2 className="text-xl font-semibold text-black mb-4">
            Products
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.4 }}
            className="flex justify-center items-center"
          >
            <p className="text-gray-600 w-4/5">
              We provide high-quality products tailored to meet farmers' needs.
              From animal feed to other agricultural supplements, we ensure the
              best solutions for your farming enterprise.
            </p>
          </motion.div>
        </div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8"
        >
          {limitedProducts.map((material, index) => (
            <motion.div key={index} variants={childVariants}>
              <Card image={material.image} title={material.title} description={material.description} />
            </motion.div>
          ))}
        </motion.div>
         <motion.div
          variants={childVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          className="text-center mt-8"
        >
          <Button text="View More" onClick={() => navigate("/services")} variant="primary" />
        </motion.div>

        {/* Raw Materials Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          className="text-center mt-16 mb-12"
        >
          <h2 className="text-xl font-semibold text-black mb-4">
            Raw Materials
          </h2>
          <p className="text-gray-600">
            We source and supply premium raw materials to support farmers and
            agricultural enterprises in achieving sustainable growth.
          </p>
        </motion.div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8"
        >
          {limitedRawmaterial.map((material, index) => (
            <motion.div key={index} variants={childVariants}>
              <Card image={material.image} title={material.title} description={material.description} />
            </motion.div>
          ))}
        </motion.div>
        <motion.div
          variants={childVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          className="text-center my-8"
        >
          <Button text="View More" onClick={() => navigate("/services")} variant="primary" />
        </motion.div>
      </div>
      <div
        id="about"
        className="h-auto bg-white flex justify-center items-center py-16 px-6 md:px-16 lg:px-40"
      >
        <div>
          <motion.div
            variants={fadeIn("up", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-black mb-4">
              Transform Farming with Our Trusted <br />
              <span className="text-primary">Animal Feed Solutions</span>
            </h2>
          </motion.div>

          {/* Mission and Vision Section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Mission and Vision Text */}
            <motion.div
              variants={fadeIn("up", 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.4 }}
              className="w-full md:w-1/2 space-y-8"
            >
              <div>
                <h3 className="text-lg font-bold text-primary my-2">
                  Our Mission
                </h3>
                <p className="text-gray-600 lg:w-3/4">
                  <span className="font-bold italic text-green-700">
                    "Empowering Farmers, Growing Together"
                  </span>
                  <br />
                  To promote and provide strategic Agri-business products,
                  technical services on livestock farming (especially chickens,
                  pigs, and dairy cattle) best practices.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary my-2">
                  Our Vision
                </h3>
                <p className="text-gray-600 lg:w-3/4">
                  <span className="font-bold italic text-green-700 my-2">
                    "Leading the Future of Farming Enterprises"
                  </span>
                  <br />
                  To achieve a leading role in Animal Feed Distribution and raw
                  materials imports and provide outstanding professional
                  services to our esteemed clients.
                </p>
              </div>
            </motion.div>

            {/* Mission and Vision Image */}
            <motion.div
              variants={fadeIn("up", 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.4 }}
              className="w-full md:w-1/2 flex justify-center"
            >
              <img
                src={chicken}
                alt="Mission"
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </div>

          {/* Why Choose Us Section */}
          <div className="flex flex-col-reverse md:flex-row items-center justify-between mt-16 gap-8">
            {/* Why Choose Us Image */}
            <motion.div
              variants={fadeIn("up", 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.4 }}
              className="w-full md:w-1/2 flex justify-center"
            >
              <img
                src={industry}
                alt="Why Choose Us"
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>

            {/* Why Choose Us Text */}
            <motion.div
              variants={fadeIn("up", 0.2)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.4 }}
              className="w-full md:w-1/2 space-y-8"
            >
              <h3 className="text-lg font-bold text-primary mb-2">
                Why Choose Us
              </h3>
              <p className="text-gray-600">
                Kigali Animal Feed Ltd is managed by a highly competent team of
                people with enormous experience in the agriculture and poultry
                sector and pig sector. All our customers have access to our
                specialists who help in any way they can.
              </p>
              <ul className="list-none space-y-4">
                <li className="flex items-center gap-4">
                  <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    1
                  </span>
                  <p className="text-gray-600">Experienced Team</p>
                </li>
                <li className="flex items-center gap-4">
                  <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    2
                  </span>
                  <p className="text-gray-600">Proven Track Record</p>
                </li>
                <li className="flex items-center gap-4">
                  <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    3
                  </span>
                  <p className="text-gray-600">Nationwide Impact</p>
                </li>
                <li className="flex items-center gap-4">
                  <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    4
                  </span>
                  <p className="text-gray-600">Customer-Centric Approach</p>
                </li>
                <li className="flex items-center gap-4">
                  <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    5
                  </span>
                  <p className="text-gray-600">
                    Dedication and Professionalism
                  </p>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
      <div
        id="contact"
        className="h-auto bg-white flex justify-center items-center py-12"
      >
     <ContactForm />
      </div>

      <div></div>
    </div>
  );
};

export default Home;
